import { FC } from 'react';

import { Heading } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useIsRatePlanMutating } from '@telialt/core-eshop/src/hooks/useIsRatePlanMutating';
import { Loader } from '@telialt/core-eshop/src/product/Loader';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { selectLeasingPlanCode, selectPossibleLeasingPlans } from '@telialt/utils/src/eshopSelectors';
import { useComponentId } from '@telialt/utils/src/hooks';

import { InitialPaymentCalculator } from './InitialPaymentCalculator/InitialPaymentCalculator';
import { LeasingPlanSelectorWrapper, RowOrderSwapper } from './LeasingPlanSelector.styles';
import { RadioFullPriceOption } from './RadioFullPriceOption';
import { RadioLeasingOption } from './RadioLeasingOption';
import { RestrictionMessageNotification } from './RestrictionMessageNotification';
import { useIsLeasingPlanMutating } from './useIsLeasingPlanMutating';

export const LeasingPlanSelector: FC = () => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();

    const possibleLeasingPlans = selectPossibleLeasingPlans(cart);

    const isLoadingLeasingPlans = useIsLeasingPlanMutating();
    const isLoadingDependencies = useIsRatePlanMutating();
    const isLoading = isLoadingLeasingPlans || isLoadingDependencies;

    const leasingPlanSelectorId = useComponentId('leasing-plan-selector');

    if (!possibleLeasingPlans?.length) {
        return (
            <LeasingPlanSelectorWrapper data-test={ESHOP_TEST_ID.LEASING_PLAN_SELECTOR}>
                <Heading variant="title-100" tag="h3" id={leasingPlanSelectorId}>
                    {t['eshop.product_page.leasing_selector.title']}
                </Heading>
                <div role="radiogroup" aria-labelledby={leasingPlanSelectorId}>
                    <RadioFullPriceOption />
                </div>
            </LeasingPlanSelectorWrapper>
        );
    }

    const selectedLeasingPlanCode = selectLeasingPlanCode(cart);
    const selectedLeasingPlanOrder =
        possibleLeasingPlans.findIndex((plan) => plan.code === selectedLeasingPlanCode) + 1;

    return (
        <LeasingPlanSelectorWrapper data-test={ESHOP_TEST_ID.LEASING_PLAN_SELECTOR}>
            <Loader isLoading={isLoading} dataTest={ESHOP_TEST_ID.LEASING_PLAN_LOADER} />
            <Heading variant="title-100" tag="h3" id={leasingPlanSelectorId}>
                {t['eshop.product_page.leasing_selector.title']}
            </Heading>
            <div
                role="radiogroup"
                aria-labelledby={leasingPlanSelectorId}
                data-test={ESHOP_TEST_ID.LEASING_RADIO_SELECTOR}
            >
                <RowOrderSwapper>
                    {possibleLeasingPlans.map((leasingPlan, index) => (
                        <RadioLeasingOption key={leasingPlan.code} leasingPlan={leasingPlan} order={index + 1} />
                    ))}
                    <InitialPaymentCalculator order={selectedLeasingPlanOrder} />
                </RowOrderSwapper>
                <RestrictionMessageNotification />
                <RadioFullPriceOption />
            </div>
        </LeasingPlanSelectorWrapper>
    );
};
